import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Flex, Button, Container, Text, useDisclosure } from '@chakra-ui/react';
import { BeakerIcon, BookOpenIcon } from '@heroicons/react/solid';
import { useMsal } from '@azure/msal-react';

import {
    getUserConnections,
    getEnvByConnectionId,
    getBooksByEnvAndConnectionId,
    setAccountConnectionConfig,
    setupDefaultConnectionHeaders,
    setURLConnectionConfig,
    getVersionsByEnvAndConnectionId,
} from '../services/connections';
import useConnectionSearch from '../hooks/useConnectionSearch';

import ConnectionStep from '../components/connection/ConnectionStep';
import ConnectionOption from '../components/connection/ConnectionOption';
import SearchBar from '../components/utils/SearchBar';
import MainModal from '../components/utils/MainModal';

import { setAccountConnection } from '../store/connection/connectionSlice';
import { getItemTypes, getPricingPoints } from '../store/item/itemApi';
import { getForecastTypes } from '../store/forecast/forecastApi';
import { getDateIntervals, getLinkedObjects } from '../store/adjustment/adjustmentApi';

import ComboboxField from '../components/forms/ComboboxField';

const Connection = () => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const history = useHistory();
    const { accounts } = useMsal();

    const defaultConnectionModal = useDisclosure();
    const switchVersionModal = useDisclosure();

    const { search, setSearch, setSearchCollection, clearSearch, noSearchResultsFound, searchResultsFound } =
        useConnectionSearch();
    const config = useSelector((state) => state.connections);

    const [currentStep, setCurrentStep] = useState(1);

    const [connection, setConnection] = useState({
        isLoading: false,
        options: [],
        error: '',
        value: config.connection,
        title: '',
    });

    const [env, setEnv] = useState({
        isLoading: false,
        options: [],
        error: '',
        value: config.environment,
        schemaGroupId: '',
        title: '',
    });

    const [book, setBook] = useState({
        isLoading: false,
        options: [],
        error: '',
        value: config.book,
        title: '',
    });

    const [versions, setVersions] = useState({
        options: [],
        temporary: { name: null, url: null, isDefault: false },
        selected: { name: null, url: null, isDefault: false },
    });

    const getConnections = useCallback(async () => {
        setConnection((prev) => ({ ...prev, isLoading: true }));

        try {
            const data = await getUserConnections();

            setConnection((prev) => ({
                ...prev,
                isLoading: false,
                options: data.map((item) => ({
                    title: item.name,
                    subtitle: '',
                    value: item.id,
                    icon: null,
                })),
            }));
        } catch (err) {
            if (err.response.status === 500) {
                setConnection((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: intl.formatMessage({ id: 'connection_error_fetch_connections' }),
                }));
            }
        }
    }, [intl]);

    useEffect(() => {
        getConnections();
    }, [getConnections]);

    const onConnectionSelect = async (selected, selectedTitle) => {
        setConnection((prev) => ({ ...prev, value: selected, title: selectedTitle }));
        setCurrentStep(2);

        setEnv((prev) => ({ ...prev, isLoading: true }));
        clearSearch();

        window.scrollTo({ top: 0 });

        const defaultVersionName = intl.formatMessage({ id: 'connection_default_version_name' });

        try {
            const data = await getEnvByConnectionId(selected);
            let versions = await getVersionsByEnvAndConnectionId(selected);

            if (versions.length > 0) {
                versions = [{ name: defaultVersionName, url: null, isDefault: true }, ...versions];
                const defaultVersion = versions.find((v) => v.isDefault);

                setVersions({
                    options: versions,
                    temporary: defaultVersion,
                    selected: defaultVersion,
                });
            }

            setEnv((prev) => ({
                ...prev,
                isLoading: false,
                options: data.map((item) => ({
                    title: item.name,
                    subtitle: item.description,
                    value: item.id,
                    schemaGroupId: item.schemaGroupId,
                    icon: BeakerIcon,
                })),
            }));
        } catch (err) {
            if (err.response.status === 500) {
                setEnv((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: intl.formatMessage({ id: 'connection_error_fetch_env' }),
                }));
            }
        }
    };

    const onEnvSelect = async (selected, schemaSelected, environmentTitle) => {
        setEnv((prev) => ({ ...prev, value: selected, schemaGroupId: schemaSelected, title: environmentTitle }));
        setCurrentStep(3);

        setBook((prev) => ({ ...prev, isLoading: true }));
        clearSearch();

        window.scrollTo({ top: 0 });

        try {
            const data = await getBooksByEnvAndConnectionId(connection.value, selected);

            if (versions.selected.isDefault || versions.options.length === 0) {
                const versions = await getVersionsByEnvAndConnectionId(connection.value, selected, true);

                if (versions.length > 0) {
                    setVersions((prev) => ({
                        ...prev,
                        selected: versions[0],
                    }));
                }
            }

            setBook((prev) => ({
                ...prev,
                isLoading: false,
                options: data.map((item) => ({
                    title: item.name,
                    subtitle: item.description,
                    value: item.id,
                    icon: BookOpenIcon,
                })),
            }));
        } catch (err) {
            if (err.response.status === 500) {
                setBook((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: intl.formatMessage({ id: 'connection_error_fetch_books' }),
                }));
            }
        }
    };

    const onVersionSelect = (selected) => {
        const temporaryVersion = versions.options.find((version) => version.url === selected);
        setVersions((prev) => ({ ...prev, temporary: temporaryVersion }));
    };

    const onVersionDismiss = (isConfirmed) => {
        if (isConfirmed) {
            setVersions((prev) => ({ ...prev, selected: versions.temporary }));
        } else {
            setVersions((prev) => ({ ...prev, temporary: versions.selected }));
        }

        switchVersionModal.onClose();
    };

    const onBookSelect = (selected, selectedTitle) => {
        setBook((prev) => ({ ...prev, value: selected, title: selectedTitle }));
        defaultConnectionModal.onOpen();
    };

    const onDismiss = (isDefaultConfig) => {
        defaultConnectionModal.onClose();

        const connectionConfigParams = new URLSearchParams({
            connection: connection.value.toString(),
            environment: env.value.toString(),
            book: book.value.toString(),
            schemaGroupId: env.schemaGroupId.toString(),
            isDefaultConfig: isDefaultConfig,
            localAccountId: accounts[0].localAccountId,
        });

        const connectionConfig = {
            connection: connection.value,
            connectionTitle: connection.title,
            environment: env.value,
            environmentTitle: env.title,
            book: book.value,
            bookTitle: book.title,
            schemaGroupId: env.schemaGroupId,
            isDefaultConfig,
        };

        dispatch(setAccountConnection(connectionConfig));

        if (isDefaultConfig) {
            setAccountConnectionConfig(connectionConfig);
            setupDefaultConnectionHeaders();
        }

        dispatch(getPricingPoints());
        dispatch(getItemTypes());
        dispatch(getForecastTypes());
        dispatch(getDateIntervals());
        dispatch(getLinkedObjects());

        if (
            window.location.origin + process.env.PUBLIC_URL === versions.selected.url ||
            versions.selected.url == null
        ) {
            if (isDefaultConfig) {
                history.push('/');
            } else {
                const url = setURLConnectionConfig(
                    connectionConfigParams,
                    window.location.origin + process.env.PUBLIC_URL
                );
                window.location.assign(url.href);
            }
        } else {
            const url = setURLConnectionConfig(connectionConfigParams, versions.selected.url);

            window.location.assign(url.href);
        }
    };

    const selectedEnv = env.value !== null ? env.options.find((e) => e.value === env.value) : null;

    const handleSearch = (value) => {
        const stages = [connection, env, book];
        const currentStage = stages[currentStep - 1];

        setSearch((prev) => ({ ...prev, term: value }));
        setSearchCollection(currentStage.options);
    };

    const searchBar = (
        <SearchBar
            minH={10}
            textHolder={intl.formatMessage(
                {
                    id: 'connection_search_placeholder',
                },
                {
                    n: currentStep,
                }
            )}
            onChange={(value) => handleSearch(value)}
        />
    );

    return (
        <Flex minHeight="100vh" bg="gray.50" pt={40} flexDirection="column">
            <MainModal
                scrollBehavior="inside"
                isOpen={defaultConnectionModal.isOpen}
                onClose={defaultConnectionModal.onClose}
                header={<FormattedMessage id="connection_set_as_default_title" />}
                content={
                    <>
                        <Text size="sm" variant="regular" mb={4}>
                            <FormattedMessage id="connection_set_as_default_p1" />
                        </Text>

                        <Text size="sm" variant="regular">
                            <FormattedMessage id="connection_set_as_default_p2" />
                        </Text>
                    </>
                }
                secondaryButton={
                    <Button textTransform="capitalize" minW={100} variant="secondary" onClick={() => onDismiss(false)}>
                        <FormattedMessage id="common_no" />
                    </Button>
                }
                footerRightSlot={
                    <Button textTransform="capitalize" minW={100} ml={4} onClick={() => onDismiss(true)}>
                        <FormattedMessage id="common_yes" />
                    </Button>
                }
            />

            <MainModal
                size="md"
                isOpen={switchVersionModal.isOpen}
                onClose={() => onVersionDismiss(false)}
                header={<FormattedMessage id="connection_select_version" />}
                content={
                    <>
                        <Text size="sm" variant="regular" mb={4}>
                            <FormattedMessage id="connection_select_version_modal_text" />
                        </Text>

                        <ComboboxField
                            my={6}
                            id="version"
                            name="version"
                            label={intl.formatMessage({ id: 'connection_select_version' })}
                            options={versions.options}
                            valueKey="url"
                            labelKey="name"
                            placeholderLabel={versions.temporary.name}
                            placeholderValue={versions.temporary.url}
                            value={versions.temporary.url}
                            onChange={onVersionSelect}
                        />
                    </>
                }
                secondaryButton={
                    <Button
                        textTransform="capitalize"
                        minW={100}
                        variant="secondary"
                        onClick={() => onVersionDismiss(false)}
                    >
                        <FormattedMessage id="common_cancel" />
                    </Button>
                }
                footerRightSlot={
                    <Button textTransform="capitalize" minW={100} ml={4} onClick={() => onVersionDismiss(true)}>
                        <FormattedMessage id="connection_select_version" />
                    </Button>
                }
            />

            <Container maxW={1056}>
                <ConnectionStep
                    search={searchBar}
                    current={currentStep}
                    step={1}
                    total={3}
                    title={<FormattedMessage id="connection_select_connection" />}
                    subtitle={<FormattedMessage id="connection_connection" />}
                    description={<FormattedMessage id="connection_please_select_the_connection" />}
                    isLoading={connection.isLoading}
                    error={connection.error}
                    noResults={noSearchResultsFound}
                    onErrorDismiss={() => {
                        setConnection((prev) => ({ ...prev, error: '' }));
                        getConnections();
                    }}
                >
                    {(searchResultsFound ? search.results : connection.options).map((o) => (
                        <ConnectionOption
                            {...o}
                            key={o.value}
                            isSelected={Number(connection.value) === o.value}
                            onClick={() => onConnectionSelect(o.value, o.title)}
                        />
                    ))}
                </ConnectionStep>

                <ConnectionStep
                    search={searchBar}
                    current={currentStep}
                    showVersionButton={versions.options.length > 0}
                    onVersionModalOpen={switchVersionModal.onOpen}
                    step={2}
                    total={3}
                    title={<FormattedMessage id="connection_select_environment" />}
                    subtitle={<FormattedMessage id="connection_environment" />}
                    description={<FormattedMessage id="connection_please_select_the_environment" />}
                    isLoading={env.isLoading}
                    error={env.error}
                    noResults={noSearchResultsFound}
                    onErrorDismiss={() => {
                        setCurrentStep(1);
                        setEnv((prev) => ({ ...prev, error: '' }));
                    }}
                >
                    {(searchResultsFound ? search.results : env.options).map((o) => (
                        <ConnectionOption
                            {...o}
                            key={o.value}
                            isSelected={env.value === o.value}
                            onClick={() => onEnvSelect(o.value, o.schemaGroupId, o.title)}
                        />
                    ))}
                </ConnectionStep>

                <ConnectionStep
                    search={searchBar}
                    current={currentStep}
                    step={3}
                    total={3}
                    title={<FormattedMessage id="connection_select_book" />}
                    subtitle={<FormattedMessage id="connection_book" />}
                    description={
                        <FormattedMessage
                            id="connection_please_select_the_book"
                            values={{ environment: selectedEnv?.title }}
                        />
                    }
                    isLoading={book.isLoading}
                    error={book.error}
                    noResults={noSearchResultsFound}
                    onErrorDismiss={() => {
                        setCurrentStep(2);
                        setBook((prev) => ({ ...prev, error: '' }));
                    }}
                >
                    {(searchResultsFound ? search.results : book.options).map((o) => (
                        <ConnectionOption
                            {...o}
                            key={o.value}
                            isSelected={Number(book.value) === o.value}
                            onClick={() => onBookSelect(o.value, o.title)}
                        />
                    ))}
                </ConnectionStep>
            </Container>
        </Flex>
    );
};

export default Connection;
